import { EvilIcons, Octicons } from '@expo/vector-icons';
import { RouteProp, useRoute, CommonActions } from '@react-navigation/native';
import React, { useState, useEffect } from 'react';
import {
  ActivityIndicator,
  Platform,
  TouchableOpacity,
  useWindowDimensions,
  View
} from 'react-native';
import styled from 'styled-components/native';
import { formatStudyLength } from '../../common/formatStudyLength';
import Button from '../../components/Button';
import ConfirmDialog from '../../components/ConfirmDialog';
import { B, H5, P } from '../../components/Typography';
import Colors from '../../constants/Colors';
import { StudiesParamList } from '../../navigation/StudiesNavigator';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { useDeleteStudyParticipant } from '../../hooks/study-participant/useDeleteStudyParticipant';
import ActivityIndicatorScreen from '../../components/ActivityIndicatorScreen';
import Spacer from '../../components/Spacer';
import AlertBox from '../../components/AlertBox';
import { useRestartStudyParticipant } from '../../hooks/study-participant/useRestartStudyParticipant';
import Link from '../../components/Link';
// import { cancelAllEventNotifications } from './helpers/ParticipantEventLocalNotificationHelper';
import { useUpdateNotificationTime } from '../../hooks/study-participant/useUpdateNotificationTime';
import FormTimeInput from '../../components/form/FormTimeInput';
import { Formik } from 'formik';
import Form from '../../components/form/Form';
import {
  convertTimeTo24Hour,
  formatTime
} from '../../dynamic-forms/plugins/form-elements/FormTimeFieldPlugin';
import { log } from '../../tools/LogHelper';
import { stringify } from 'flatted';
import { useWithdrawStudyParticipant } from '../../hooks/study-participant/useWithdrawStudyParticipant';
import elementIDs from '../../../elementIDs';

const StyledScrollView = styled.ScrollView`
  background: ${Colors.background};
`;

const StyledView = styled.View`
  margin-bottom: 10px;
  align-items: center;
  margin: 26px;
`;

const StyledContainerView = styled.View`
  margin-top: 8px;
  max-width: 540px;
  width: 100%;
`;

const StyledEditStudyContainerView = styled.View`
  justify-content: space-between;
`;

const StyledStudyActionContainer = styled.View`
  width: 100%;
  max-width: 540px;
  align-items: center;
  margin: 30px 0 12px 0;
`;

const StyledEditStudyFieldContainer = styled.View`
  flex-grow: 1;
  width: 100%;
  background-color: white;
  margin-bottom: 12px;
  border: 2px solid #cfcaf6;
  border-radius: 16px;
  padding: 16px;
  min-height: 80px;
  justify-content: center;
`;

const StyledB = styled(B)`
  font-size: 18px;
  margin-bottom: 8px;
`;

const StyledFooter = styled(P)`
  text-align: center;
  margin: 0px 8px;
`;

const StyledEditIcon = styled.TouchableOpacity`
  margin-left: 10px;
`;

const StyledRestartStudyButton = styled(Button)`
  width: 100%;
`;

// const StyledCommPrefsSwitchContainer = styled.View`
//   flex-direction: row;
//   align-items: center;
//   margin-bottom: 20px;
//   width: 270px;
// `;

// const StyledCustomSwitch = styled(CustomToggleSwitch)`
//   margin-right: 12px;
// `;

// const StyledP = styled(P)`
//   letter-spacing: -0.23px;
//   padding-right: 25px;
// `;

const StyledNotificatonTimeTitleContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const StyledNotificationTimeButtonContainer = styled.View<{
  isMobile: boolean;
}>`
  ${Platform.OS === 'web' ? 'margin-top: 8px;' : ''}
  flex-direction: ${p => (!p.isMobile ? 'row' : 'column')};
  justify-content: ${p => (!p.isMobile ? 'space-around' : 'flex-start')};
`;

const StyledLink = styled(Link)`
  color: ${Colors.primary};
`;

const StyleNotificationTimeButton = styled(Button)<{
  isMobile: boolean;
}>`
  width: ${p => (!p.isMobile ? '48%' : '100%')};
`;

const StudyEditScreen: React.FC = () => {
  const window = useWindowDimensions();
  const isMobile = window.width <= 500;
  // const [makeStudyPublic, setMakeStudyPublic] = useState(false);
  // const [pauseStudy, setPauseStudy] = useState(false);
  const [confirm, setConfirm] = useState<'delete' | 'restart' | 'withdraw'>();
  const route = useRoute<RouteProp<StudiesParamList, 'StudyEdit'>>();

  const startDate = route.params.startDate;
  const studyName = route.params.studyName;
  const studyLength = route.params.studyLength;
  const studyId = route.params.studyId;
  const studyParticipantId = route.params.studyParticipantId;
  const notificationTime = route.params.notificationTime;
  const allowSelfWithdraw = route.params.allowSelfWithdraw;
  const studyType = route.params.studyType;
  const navigation = useNavigation<StackNavigationProp<any>>();
  const [editNotifTime, setEditNotifTime] = useState<boolean>();
  let displayedNotificationTime = 'No notification time set';
  let formattedNotifTime = undefined;

  if (notificationTime) {
    formattedNotifTime = formatTime(notificationTime);
    displayedNotificationTime =
      formattedNotifTime.time + ' ' + formattedNotifTime.ampm.toLowerCase();
  }

  // Customize the app header
  useEffect(() => {
    navigation.setOptions({
      header: undefined,
      headerStyle: {
        backgroundColor: '#FDFBF7',
        elevation: 0,
        shadowOpacity: 0,
        borderBottomWidth: 0,
        height: Platform.OS !== 'ios' ? 85 : undefined
      },
      headerLeft: (props: any) => (
        <TouchableOpacity
          style={{ marginLeft: 26, width: 30 }}
          onPress={() => {
            if (!props.canGoBack) {
              const parentNavigation = navigation?.getParent();
              if (!parentNavigation) return;
              const parentCanGoBack = parentNavigation.canGoBack();
              if (!parentCanGoBack) {
                navigation.reset({
                  index: 0,
                  routes: [
                    {
                      name: 'StudiesNav'
                    }
                  ]
                });
              } else {
                parentNavigation.goBack();
              }
            } else {
              props.onPress?.();
            }
          }}
        >
          <Octicons size={30} name='chevron-left' />
        </TouchableOpacity>
      ),
      headerTitle: () => <H5 color='dark'>Study Information</H5>,
      headerTitleAlign: 'center',
      headerShown: true,
      headerMode: 'float',
      gestureEnabled: false
    });
  }, [navigation]);

  // hooks
  const {
    deleteResult,
    deletingStudyParticipant,
    deleteStudyParticipantError,
    deleteStudyParticipant
  } = useDeleteStudyParticipant(studyParticipantId);
  const {
    restartedStudyParticipant,
    restartingStudyParticipant,
    restartingStudyParticipantError,
    restartStudyParticipant
  } = useRestartStudyParticipant(studyParticipantId);

  const {
    withdrawnStudyParticipant,
    withdrawingStudyParticipant,
    withdrawingStudyParticipantError,
    withdrawStudyParticipant
  } = useWithdrawStudyParticipant(studyParticipantId);

  const {
    updatedNotificationTime,
    updatingNotificationTime,
    updateNotificationTime
  } = useUpdateNotificationTime(studyParticipantId);

  useEffect(() => {
    if (!updatedNotificationTime) return;
    navigateToMyStudiesScreen(studyId, studyParticipantId);
    setEditNotifTime(false);
  }, [navigation, updatedNotificationTime]);

  useEffect(() => {
    if (deleteResult) {
      navigation.popToTop();
    }
  }, [deleteResult, navigation]);

  const navigateToMyStudiesScreen = (
    studyId: number,
    studyParticipantId: number
  ) => {
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [
          {
            name: 'DrawerNavigator',
            state: {
              index: 0,
              routes: [
                {
                  name: 'BottomTabNav',
                  state: {
                    index: 0,
                    routes: [
                      {
                        name: 'StudiesNav',
                        state: {
                          index: 1,
                          routes: [
                            { name: 'MyStudies' },
                            {
                              name: 'StudyInfo',
                              params: {
                                studyId: studyId,
                                studyParticipantId: studyParticipantId
                              }
                            }
                          ]
                        }
                      }
                    ]
                  }
                }
              ]
            }
          }
        ]
      })
    );
  };

  useEffect(() => {
    if (withdrawnStudyParticipant) {
      navigateToMyStudiesScreen(
        withdrawnStudyParticipant.studyId,
        withdrawnStudyParticipant.id
      );
    }
  }, [withdrawnStudyParticipant, navigation]);

  useEffect(() => {
    if (restartedStudyParticipant) {
      navigateToMyStudiesScreen(
        restartedStudyParticipant.studyId,
        restartedStudyParticipant.id
      );
    }
  }, [restartedStudyParticipant, navigation]);

  return (
    <StyledScrollView
      scrollIndicatorInsets={{ right: 1 }}
      contentContainerStyle={{
        flexGrow: 1,
        justifyContent: 'space-between'
      }}
    >
      <StyledView>
        <StyledContainerView>
          <StyledEditStudyContainerView>
            <StyledEditStudyFieldContainer>
              <StyledB color='dark'>Name</StyledB>
              <P color='dark'>{studyName}</P>
            </StyledEditStudyFieldContainer>
            <StyledEditStudyFieldContainer>
              <StyledB color='dark'>Start Date</StyledB>
              <P color='dark'>{startDate || 'N/A'}</P>
            </StyledEditStudyFieldContainer>
            <StyledEditStudyFieldContainer>
              <StyledB color='dark'>Duration</StyledB>
              <P color='dark'>{formatStudyLength(studyLength)}</P>
            </StyledEditStudyFieldContainer>
            <StyledEditStudyFieldContainer>
              <StyledNotificatonTimeTitleContainer>
                <View>
                  <StyledB color='dark'>Notification Time</StyledB>
                  {!editNotifTime && (
                    <P color='dark'>{displayedNotificationTime}</P>
                  )}
                </View>
                {formattedNotifTime && !editNotifTime && (
                  <StyledEditIcon
                    activeOpacity={0.7}
                    testID={elementIDs.notificationTimeID}
                    onPress={() => setEditNotifTime(!editNotifTime)}
                  >
                    <EvilIcons name='chevron-right' size={40} color='black' />
                  </StyledEditIcon>
                )}
              </StyledNotificatonTimeTitleContainer>

              {formattedNotifTime && editNotifTime && (
                <Formik
                  initialValues={{
                    time: formattedNotifTime.time,
                    timeOfDay: formattedNotifTime.ampm
                  }}
                  onSubmit={values => {
                    log(stringify(values));
                  }}
                >
                  {values => (
                    <Form>
                      <FormTimeInput name='time' />
                      <Spacer size={'medium'} />
                      <StyledNotificationTimeButtonContainer
                        isMobile={isMobile}
                      >
                        <StyleNotificationTimeButton
                          isMobile={isMobile}
                          testID={elementIDs.notificationTimeSubmitID}
                          disabled={updatingNotificationTime}
                          endIcon={
                            updatingNotificationTime ? (
                              <ActivityIndicator />
                            ) : (
                              <></>
                            )
                          }
                          onPress={() => {
                            const value = convertTimeTo24Hour(
                              values.values.time,
                              values.values.timeOfDay
                            );
                            updateNotificationTime({
                              preferredNotificationTime: value
                            });
                          }}
                        >
                          Submit
                        </StyleNotificationTimeButton>
                        <Spacer size={'small'} />
                        <StyleNotificationTimeButton
                          isMobile={isMobile}
                          disabled={updatingNotificationTime}
                          outlined
                          onPress={() => setEditNotifTime(false)}
                        >
                          Cancel
                        </StyleNotificationTimeButton>
                      </StyledNotificationTimeButtonContainer>
                    </Form>
                  )}
                </Formik>
              )}
            </StyledEditStudyFieldContainer>
            {/* NOTE: comment out for now, we'll add this back later */}
            {/*<View style={{ marginTop: 4 }}>
              <StyledCommPrefsSwitchContainer>
                <StyledCustomSwitch
                  color='light'
                  value={makeStudyPublic}
                  onToggle={() => {
                    setMakeStudyPublic(previousState => !previousState);
                  }}
                />
                <StyledP color='light'>Make Study Public</StyledP>
              </StyledCommPrefsSwitchContainer>
              <StyledCommPrefsSwitchContainer>
                <StyledCustomSwitch
                  color='light'
                  value={pauseStudy}
                  onToggle={() => {
                    if (!pauseStudy) {
                      setDisplayConfirmDialog(true);
                    } else {
                      setPauseStudy(previousState => !previousState);
                    }
                  }}
                />
                <StyledP color='light'>Pause Study</StyledP>
              </StyledCommPrefsSwitchContainer>
                </View>*/}
          </StyledEditStudyContainerView>
        </StyledContainerView>
        <Spacer size={4} />
        {route.params.type === 'planning' ? (
          <>
            <StyledStudyActionContainer>
              <Button
                testID={elementIDs.deleteStudyID}
                outlined
                style={{ width: '60%' }}
                onPress={() => {
                  setConfirm('delete');
                }}
              >
                Delete Study
              </Button>
            </StyledStudyActionContainer>
            <StyledFooter padding={1}>This will delete the study</StyledFooter>
          </>
        ) : /*studyType === StudyType.Sponsor ? (*/
        allowSelfWithdraw ? (
          <>
            <StyledStudyActionContainer>
              <Button
                testID={elementIDs.withdrawStudyID}
                outlined
                style={{ width: '60%' }}
                onPress={() => {
                  setConfirm('withdraw');
                }}
              >
                Withdraw
              </Button>
            </StyledStudyActionContainer>
            <StyledFooter padding={1}>
              This will withdraw you from the study
            </StyledFooter>
          </>
        ) : (
          <></>
          // )
          // ) : (
          // <>
          //   <StyledStudyActionContainer>
          //     <StyledRestartStudyButton
          //       color='#FFE8DA'
          //       testID={elementIDs.restartStudyID}
          //       onPress={() => {
          //         setConfirm('restart');
          //       }}
          //     >
          //       Restart Study
          //     </StyledRestartStudyButton>
          //   </StyledStudyActionContainer>
          //   <Spacer size={1} />
          //   <StyledFooter color='dark'>
          //     This will erase all of your existing data. You can also{' '}
          //     <StyledLink inverse onPress={() => setConfirm('delete')}>
          //       delete
          //     </StyledLink>{' '}
          //     the study
          //     {allowSelfWithdraw && (
          //       <StyledFooter color='dark'>
          //         {' '}
          //         or{' '}
          //         <StyledLink inverse onPress={() => setConfirm('withdraw')}>
          //           withdraw
          //         </StyledLink>{' '}
          //         from the study
          //       </StyledFooter>
          //     )}
          //   </StyledFooter>
          // </>
        )}
        {Boolean(deleteStudyParticipantError) && (
          <AlertBox severity='error'>
            {deleteStudyParticipantError?.message}
          </AlertBox>
        )}
        {Boolean(restartingStudyParticipantError) && (
          <AlertBox severity='error'>
            {restartingStudyParticipantError?.message}
          </AlertBox>
        )}
        {Boolean(withdrawingStudyParticipantError) && (
          <AlertBox severity='error'>
            {withdrawingStudyParticipantError?.message}
          </AlertBox>
        )}
        <Spacer size={2} />
        {/* {displayConfirmDialog && (
          <ConfirmDialog
            title='Are you sure you want to pause your study?'
            message='While pausing your study is a possibility; keep in mind that it might also affect and alter the results of your study and outcome as a whole.'
            confirmLabel='Confirm Pause'
            cancelLabel='maybe later'
            onConfirmButtonClicked={() => {
              setDisplayConfirmDialog(false);
              setPauseStudy(true);
            }}
            onCancelButtonClicked={() => {
              setDisplayConfirmDialog(false);
            }}
          />
        )} */}
        {confirm === 'delete' ? (
          <ConfirmDialog
            title='Are you sure you want to delete the study?'
            message='Deleting the study will remove the study from your profile.'
            confirmLabel='Yes'
            cancelLabel='No'
            onConfirmButtonClicked={() => {
              setConfirm(undefined);
              // cancelAllEventNotifications(studyParticipantId);
              deleteStudyParticipant();
            }}
            onCancelButtonClicked={() => {
              setConfirm(undefined);
            }}
          />
        ) : confirm === 'restart' ? (
          <ConfirmDialog
            title='Are you sure you want to restart the study?'
            message='Restarting the study will erase all study data you have entered so far.'
            confirmLabel='Yes'
            cancelLabel='No'
            onConfirmButtonClicked={() => {
              setConfirm(undefined);
              // cancelAllEventNotifications(studyParticipantId);
              restartStudyParticipant();
            }}
            onCancelButtonClicked={() => {
              setConfirm(undefined);
            }}
          />
        ) : (
          confirm === 'withdraw' && (
            <ConfirmDialog
              title='Are you sure you want to withdraw from the study?'
              message='Withdrawing from the study will mark you as withdrawn and events will no longer be accessible'
              confirmLabel='Yes'
              cancelLabel='No'
              onConfirmButtonClicked={() => {
                setConfirm(undefined);
                withdrawStudyParticipant();
              }}
              onCancelButtonClicked={() => {
                setConfirm(undefined);
              }}
            />
          )
        )}
      </StyledView>
      {Boolean(deletingStudyParticipant) ||
        Boolean(restartingStudyParticipant) ||
        (Boolean(withdrawingStudyParticipant) && (
          <ActivityIndicatorScreen modal />
        ))}
    </StyledScrollView>
  );
};

export default StudyEditScreen;
